import { useCallback, useEffect, useState } from "react";
import { PITCH_H, PITCH_W } from "@/lib/canvas/coords";

/**
 * Canvas-envelope sizing — measures a CSS-sized 16:9 envelope and
 * derives pixel dimensions for the Konva stage(s) inside it.
 *
 * Why split CSS from JS:
 *   The envelope's *size* is owned by CSS (`aspect-ratio: 16/9` plus
 *   a `max-height` cap AND a matching `max-width` derived from the
 *   same cap). The browser resolves both axes; JS only READS the
 *   result. That makes the read/write loop impossible by
 *   construction — the observed element's size is determined by CSS
 *   layout, not by anything this hook computes.
 *
 *   Konva, in contrast, needs *pixel* width/height props for its
 *   `<Stage>`. The hook converts the measured envelope into:
 *     - `envelopeWidth/Height`: pass to `TelestratorCanvas` (16:9).
 *     - `boardWidth/Height`: pass to `TacticalBoardView`; the pitch
 *       aspect (~1.544:1) is narrower than 16:9 (~1.778:1), so the
 *       pitch fits the envelope by height with horizontal padding —
 *       sidelines that previously sat at the canvas edge stay
 *       visible inside the frame.
 *
 * The hook exposes a CALLBACK REF (`setRef`) instead of accepting
 * a `RefObject`. With a callback ref the node is tracked in state,
 * so a keyed remount of the observed element (e.g. the playlist
 * envelope keyed by `session.playKey`) fires the setter again and
 * the observer re-attaches to the live node. A plain `RefObject`
 * passed into `useEffect` only re-runs on dependency change, so
 * after a remount the observer would stay bound to the detached
 * old node and miss subsequent resizes.
 *
 * Usage:
 *   ```tsx
 *   const env = useCanvasEnvelope();
 *   return (
 *     <div ref={env.setRef}
 *          style={{ aspectRatio: "16 / 9",
 *                   maxHeight: "min(60vh, calc(95vh - 360px))",
 *                   maxWidth: "calc(min(60vh, calc(95vh - 360px)) * 16 / 9)",
 *                   width: "100%" }}>
 *       <TacticalBoardView width={env.boardWidth} ... />
 *     </div>
 *   );
 *   ```
 */
export type CanvasEnvelope = {
  /** Callback ref — pass as `ref={env.setRef}` on the envelope div. */
  setRef: (node: HTMLDivElement | null) => void;
  /** Width of the 16:9 envelope in pixels (resolved by CSS). */
  envelopeWidth: number;
  /** Height of the 16:9 envelope in pixels (resolved by CSS). */
  envelopeHeight: number;
  /** Pitch width to pass to `TacticalBoardView` so the board fits
   * the envelope by height (horizontal padding on the sides). */
  boardWidth: number;
  /** Pitch height — equal to `envelopeHeight`. */
  boardHeight: number;
};

const PITCH_ASPECT_W_OVER_H = PITCH_W / PITCH_H;
// Floor at 32px so the Konva pitch background (which subtracts an
// 8px pad and then takes `Math.min(innerW, innerH) * 0.097` for the
// centre-circle radius) never goes negative on the first frame when
// the ResizeObserver hasn't fired yet.
const MIN_DIMENSION = 32;

export function useCanvasEnvelope(): CanvasEnvelope {
  // Seed with the floor; the first ResizeObserver tick (which fires
  // synchronously on observe()) replaces it with the real size before
  // the next paint.
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: MIN_DIMENSION,
    height: MIN_DIMENSION,
  });
  // Node is held in state via a callback ref. When the caller's
  // observed div remounts (e.g. due to a `key` change), React calls
  // setRef(null) then setRef(newNode), which retriggers the effect
  // below and re-binds the observer to the live node.
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const setRef = useCallback((el: HTMLDivElement | null) => {
    setNode(el);
  }, []);

  useEffect(() => {
    if (!node) return;
    const update = (w: number, h: number) => {
      setSize((prev) =>
        prev.width === w && prev.height === h ? prev : { width: w, height: h },
      );
    };
    const rect = node.getBoundingClientRect();
    update(Math.floor(rect.width), Math.floor(rect.height));
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        update(
          Math.floor(entry.contentRect.width),
          Math.floor(entry.contentRect.height),
        );
      }
    });
    obs.observe(node);
    return () => obs.disconnect();
  }, [node]);

  // Even with `aspect-ratio: 16/9` + `max-height` + `max-width` on
  // the observed div, a parent flex column with `min-height: 0` can
  // shrink the rendered height below the 16:9 ideal. In that case
  // the measured box is taller-OR-wider than the true 16:9 fit, so
  // we collapse to the largest 16:9 rectangle that still fits inside
  // the measured box. That keeps the Konva stages we hand back
  // letterboxed inside the CSS frame instead of overflowing it.
  const measuredW = Math.max(MIN_DIMENSION, size.width);
  const measuredH = Math.max(MIN_DIMENSION, size.height);
  const fitByHeight = Math.floor(measuredH * (16 / 9));
  const envelopeWidth = Math.min(measuredW, fitByHeight);
  const envelopeHeight = Math.floor(envelopeWidth * (9 / 16));
  const boardHeight = envelopeHeight;
  const boardWidth = Math.max(
    MIN_DIMENSION,
    Math.floor(boardHeight * PITCH_ASPECT_W_OVER_H),
  );

  return { setRef, envelopeWidth, envelopeHeight, boardWidth, boardHeight };
}
